import { logger } from "@swa-ui/log";

import { EVENT_TYPES, VENDOR_SOURCE, VENDOR_VERSION } from "../defines/ppaHotelDefaults";

/**
 * Parses the origin from a URL string for `postMessage` validation. Returns `null` when the
 * URL can't be parsed — but by the time we call this, `isHotelIFrameDataValid` has already
 * verified the URL is parseable and rejected malformed values upstream, so reaching the catch
 * means the container was rendered without the upstream gate and is a real bug worth surfacing.
 */
export const getOrigin = (url) => {
  let origin = null;

  try {
    ({ origin } = new URL(url));
  } catch (error) {
    logger.error("PPAHotelContainer: could not parse iframe origin", {
      component: "PPAHotelContainer",
      error,
      url,
    });
  }

  return origin;
};

/**
 * Extracts the iframe resize height from a vendor `crossSellResize` message payload.
 * Supports both the vendor's real shape (`payload.size.height`) and the PDF-documented
 * fallback shapes (`payload.height`, `data.height`).
 */
export const getResizeHeight = (message) =>
  message.payload?.size?.height ?? message.payload?.height ?? message.data?.height;

/**
 * Routes an incoming vendor `postMessage` payload to the appropriate caller-supplied callback.
 *
 * Validates the message shape (non-null object) and vendor markers (`source`/`version`)
 * before dispatching. Vendor markers are optional — the PDF-documented shape does not mandate
 * them — so a missing marker is treated as valid to tolerate both the real and PDF shapes.
 * The origin + `contentWindow` checks performed by the caller are the actual security boundary.
 *
 * @param {*} message - the `event.data` payload from a `message` event
 * @param {Object} callbacks - per-event dispatchers
 * @param {Function} callbacks.onError - invoked with the vendor error code on `crossSellError`
 * @param {Function} callbacks.onReady - invoked with the payload on `crossSellReady`
 * @param {Function} callbacks.onResize - invoked with the resolved pixel height on `crossSellResize`
 * @param {Function} [callbacks.onUpdate] - optional; invoked with the payload on `crossSellUpdate`
 */
export const processEvent = (message, callbacks) => {
  const { onError, onReady, onResize, onUpdate } = callbacks;
  const isObjectMessage = message !== null && typeof message === "object";

  if (isObjectMessage) {
    const isValidSource = message.source == null || message.source === VENDOR_SOURCE;
    const isValidVersion = message.version == null || message.version === VENDOR_VERSION;

    if (isValidSource && isValidVersion) {
      const eventType = message.type;
      const payload = message.payload ?? message.data;

      if (eventType === EVENT_TYPES.READY && onReady) {
        onReady(payload);
      } else if (eventType === EVENT_TYPES.RESIZE && onResize) {
        onResize(getResizeHeight(message));
      } else if (eventType === EVENT_TYPES.UPDATE && onUpdate) {
        onUpdate(payload);
      } else if (eventType === EVENT_TYPES.ERROR && onError) {
        onError(message.payload?.code ?? message.error);
      }
    }
  }
};
